import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Panel, TokenLogo, VerifiedBadge, StarButton, StatBox } from "../components/common/Panel";
import { getTokens, getAddress, getAddressTokens } from "../lib/api";
import { shorten, fmtNum, fmtEth, CHAIN } from "../lib/format";
import { useWatchlist } from "../hooks/useWatchlist";

export default function Watchlist() {
  const { list, isStarred, toggle } = useWatchlist();
  const [tokens, setTokens] = useState([]);
  const [wallets, setWallets] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTokens().then((d) => setTokens(d.tokens || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const tokenMap = useMemo(() => {
    const m = {};
    tokens.forEach((t) => { m[t.address.toLowerCase()] = t; });
    return m;
  }, [tokens]);

  const starredTokens = useMemo(() => list.map((a) => tokenMap[a]).filter(Boolean), [list, tokenMap]);
  const walletAddrs = useMemo(() => list.filter((a) => !tokenMap[a]), [list, tokenMap]);

  // fetch native balance + holdings for starred non-token addresses
  useEffect(() => {
    let alive = true;
    walletAddrs.forEach((a) => {
      if (wallets[a]) return;
      Promise.all([getAddress(a).catch(() => null), getAddressTokens(a).catch(() => ({ holdings: [] }))])
        .then(([info, tk]) => {
          if (!alive || !info) return;
          setWallets((prev) => ({ ...prev, [a]: { ...info, tokenCount: (tk.holdings || []).length } }));
        });
    });
    return () => { alive = false; };
  }, [walletAddrs]); // eslint-disable-line

  const walletList = walletAddrs.map((a) => wallets[a]).filter(Boolean);
  const totalBcc = walletList.reduce((s, w) => s + (w.balanceEth || 0), 0);

  const empty = !loading && list.length === 0;

  return (
    <div className="fade-in" data-testid="watchlist-page">
      <div className="mb-4">
        <span className="eyebrow" style={{ color: "var(--amber)" }}>Your Dashboard</span>
        <h1 className="font-display text-[24px] font-extrabold mt-1">Watchlist</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>Everything you've starred — tokens, wallets and contracts — in one place.</p>
      </div>

      {empty ? (
        <Panel testId="watchlist-empty" menu={false}>
          <div className="p-12 text-center">
            <i className="fa-solid fa-star text-[28px] mb-4" style={{ color: "var(--border-2)" }} />
            <div className="font-display text-[16px] font-semibold mb-1">Your watchlist is empty</div>
            <p className="text-[13px] mb-4" style={{ color: "var(--muted)" }}>Star a token or address anywhere in the explorer to pin it here.</p>
            <Link to="/tokens" className="btn-amber inline-block py-2 px-5 text-[12px]" data-testid="watchlist-browse-tokens">Browse Tokens →</Link>
          </div>
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <StatBox testId="wl-stat-total" label="Starred Items" value={list.length} sub="tokens + addresses" icon="fa-solid fa-star" />
            <StatBox testId="wl-stat-tokens" label="Tokens" value={starredTokens.length} sub="BCC-20 assets" icon="fa-solid fa-coins" accent="var(--green)" />
            <StatBox testId="wl-stat-wallets" label="Wallets & Contracts" value={walletAddrs.length} sub="addresses" icon="fa-solid fa-wallet" accent="var(--blue)" />
            <StatBox testId="wl-stat-bcc" label="Combined Balance" value={`${fmtEth(totalBcc, 2)}`} sub={`${CHAIN.symbol} across wallets`} icon="fa-solid fa-sack-dollar" />
          </div>

          <Panel title="Starred Tokens" subtitle={`${starredTokens.length}`} className="mb-5" bodyClass="overflow-auto" testId="panel-watchlist-tokens">
            <table className="dt">
              <thead><tr><th style={{ width: 40 }}></th><th>Token</th><th>Symbol</th><th>Contract</th><th>Total Supply</th><th>Holders</th><th>Transfers</th></tr></thead>
              <tbody>
                {starredTokens.map((t, i) => (
                  <tr key={t.address} onClick={() => (window.location.href = `/token/${t.address}`)} data-testid={`watchlist-token-${i}`}>
                    <td><StarButton starred={isStarred(t.address)} onToggle={() => toggle(t.address)} testId={`wl-unstar-token-${i}`} /></td>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <TokenLogo logo={t.logo} symbol={t.symbol} size={24} />
                        <span className="font-display font-semibold">{t.name}</span>
                        {t.verified && <VerifiedBadge small />}
                      </div>
                    </td>
                    <td><span className="pill pill-amber">{t.symbol}</span></td>
                    <td><Link to={`/token/${t.address}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{shorten(t.address, 8, 6)}</Link></td>
                    <td className="mono">{fmtNum(t.totalSupply)}</td>
                    <td className="mono">{fmtNum(t.holders)}</td>
                    <td className="mono" style={{ color: "var(--muted)" }}>{fmtNum(t.transfers)}</td>
                  </tr>
                ))}
                {starredTokens.length === 0 && <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--muted)" }}>No tokens starred yet.</td></tr>}
              </tbody>
            </table>
          </Panel>

          <Panel title="Starred Wallets & Contracts" subtitle={`${walletAddrs.length}`} bodyClass="overflow-auto" testId="panel-watchlist-wallets">
            <table className="dt">
              <thead><tr><th style={{ width: 40 }}></th><th>Address</th><th>Type</th><th>Balance</th><th>Tokens Held</th></tr></thead>
              <tbody>
                {walletAddrs.map((a, i) => {
                  const w = wallets[a];
                  return (
                    <tr key={a} onClick={() => (window.location.href = `/address/${a}`)} data-testid={`watchlist-wallet-${i}`}>
                      <td><StarButton starred={isStarred(a)} onToggle={() => toggle(a)} testId={`wl-unstar-wallet-${i}`} /></td>
                      <td><Link to={`/address/${a}`} className="link-amber mono text-[12px]" onClick={(e) => e.stopPropagation()}>{w ? w.address : a}</Link></td>
                      <td>{w ? (w.isValidator ? <span className="pill pill-amber">Validator</span> : w.isContract ? <span className="pill pill-green">Contract</span> : <span className="pill pill-muted">Wallet</span>) : <span className="pill pill-muted">…</span>}</td>
                      <td className="mono">{w ? `${fmtEth(w.balanceEth, 4)} ${CHAIN.symbol}` : "…"}</td>
                      <td className="mono" style={{ color: "var(--muted)" }}>{w ? w.tokenCount : "…"}</td>
                    </tr>
                  );
                })}
                {walletAddrs.length === 0 && <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--muted)" }}>No wallets or contracts starred yet.</td></tr>}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}
